import {GraphQL} from "./GraphQL";
import {cuppa, log, val} from "../../cuppa/cuppa";
import {Constants} from "./Constants";
import {CuppaStorage} from "../../cuppa/cuppa.storage";
import {Storages} from "./Storages";
import {UserCtrl} from "./UserCtrl";
import {SocketManager} from "./SocketManager";
import {OrganizationCtrl} from "./OrganizationCtrl";
import {Globals} from "./Globals";
import {Alert} from "../components/commond/Alert";
import {Language} from "./Language";
import {NotificationCtrl} from "./NotificationCtrl";

export class ConversationsCtrl{
	static async loadConversations({
		organizationId,
    page = 1,
    quantity = Constants.QUANTITY,
    condition = '',
    orderBy = 'date ASC',
    questionsFilter = '',
    assessmentFilter = null,
    showFilter = null,
		requestQuestions = true,
		requestLastAssignedUser = true,
    searchInAllAssessments = false,
	} = {}){
		if(!organizationId) return;
		let questions = (!requestQuestions) ? `` : `
			questions{
	      id, question_id, answer_id, answer_value
	      question{
	        id, question, abbreviation, required, order, display_in_list, organization_id,
	      }
	      answer{
	        id, question_id, answer, status
	      }
	    }
		`
		let lastAssignedUser = (!requestLastAssignedUser) ? `` : `
			last_assigned_user{
				id, name, last_name, email, role{
				  id, role
				}
	    }
		`
		let query = `
			conversations(organizationId:${organizationId}, page:${page}, quantity:${quantity}, condition:"${condition}", orderBy:"${orderBy}", questionsFilter:"${questionsFilter}", assessmentFilter:${assessmentFilter || null}, searchInAllAssessments:${cuppa.boolean(searchInAllAssessments)}, showFilter:"${showFilter}"){
		    page, pages, totalRows
		    data {
		      id, organization_id, anonymous_user_id, status, date, last_assigned_user_id, description, last_interaction_date, last_assessment_date,
		      ${questions}
		      ${lastAssignedUser}
		      last_assessment{
		        id, conversation_id, user_id, assessed_status, comment, date,
		      }
		      last_note{
           id, note, date, user{
             id, name, last_name
           }
         }
		    }
		  }
		`
		return await GraphQL.query({query});
	}

	static async loadConversation({id} = {}) {
		let query = `
			conversation(id:${id}){
		    id, organization_id, anonymous_user_id, status, date, last_assigned_user_id, description,
	      questions{
          id, conversation_id, question_id, answer_id, answer_value,
          question{
            id, question, abbreviation, required, order, organization_id, 
          }
          answer{
            id, question_id, answer, status
          }
        }
        last_assigned_user{
		      id, name, last_name, name, email, mobile, status, 
		      role{
		        id, role
		      }
		    }
		  }
		`
		return await GraphQL.query({query});
	}

	static async loadConversationMessages({conversationId, page = 1}){
		if(!conversationId) return;
		let query = `
			conversationMessages(conversationId:${conversationId}, page:${page}){
		    page, pages, totalRows, 
		    data { 
		      id, user_id, anonymous_user_id, message, date 
		      user{
	          id, name,
	        } 
	      }
		  }
		`
		return await GraphQL.query({query});
	}

	static async loadNotes({conversationId, orderBy = 'date ASC'}){
		if(!conversationId) return;
		let query = `
			conversationNotes(conversationId:${conversationId}, orderBy:"${orderBy}"){
		    page, pages, totalRows, data{
		      id, conversation_id, user_id, note, date,
		      user{
            id, name, 
            role{
              id, role
            }
          }
		    }
		  }
		`
		return await GraphQL.query({query});
	}

	static async loadAssessments({conversationId, orderBy = 'date ASC'}){
		if(!conversationId) return;
		let user = UserCtrl.getUser();
		let role = UserCtrl.getRole();
		let cond = ``;
		let query = `#graphql
    {
      conversationAssessments(conversationId:${conversationId}, condition:"${cond}", orderBy:"${orderBy}"){
        page, pages, totalRows, data{
          id, conversation_id, user_id, comment, assessed_status, date,
          user{
            id, name,
            role{
              id, role
            }
          },
          questions{
            id, question_id, answer_id, answer_value, date,
            question{
              id, question, order, type, metadata
            }
            answer{
              id, answer
            }
          }
        }
      }
    }
		`
		return await GraphQL.query({query, addRootBrackets:false});
	}

	static async setConversation({conversation}){
		if(!conversation) conversation = {};
		let organization = await CuppaStorage.getData(Storages.organization);
		let user = await CuppaStorage.getData(Storages.user);
		// new conversation
			if(!conversation.organization_id) conversation.organization_id = organization.id;
			if(!conversation.status) conversation.status = Constants.conversationStatus.ACTIVE;
			if(!conversation.last_assigned_user_id) conversation.last_assigned_user_id = user.id;
			if(!conversation.anonymous_user_id) delete conversation.anonymous_user_id;
		// set answers
			conversation.answers = (conversation.questions || []).map(question=>{
				let answer = { question_id:question.question_id };
				if(question.answer_id) answer.answer_id = question.answer_id;
				if(question.answer_value) answer.answer_value = question.answer_value;
				return answer;
			});
		// create mutation
		let types = {status:'enum', priority:'enum', description:'multiline', last_assigned_user:'remove', questions:'remove', answer_value:'multiline', created_by:'enum', created_by_id:'number'};
		let mutation = `
			setConversation(conversation:${cuppa.dataToGraphQLInput(conversation, {types})}){
				id, organization_id, anonymous_user_id, status, date, last_assigned_user_id, description
			}
		`;
		return await GraphQL.query({mutation});
	}

	static async setConversationNote({note}){
		if(!note?.note) return;
		let types = {note:"multiline"};
		let mutation = `
			setConversationNote(note:${cuppa.dataToGraphQLInput(note,  {types})}){
		    id, conversation_id, user_id, note, date
		  }
		`
		return await GraphQL.query({mutation});
	}

	static async setConversationAssessment({assessment}){
		if(!assessment?.assessed_status) return;
		// set answers
			assessment.answers = (assessment.questions || []).map(question=>{
				return { question_id:question.question_id, answer_id:question.answer_id || null, answer_value:question.answer_value || null }
			});
		let types = {assessed_status:'number', user:'remove', comment:"multiline", questions:'remove', answer_value:'multiline'};
		let mutation = `
		  setConversationAssessment(assessment:${cuppa.dataToGraphQLInput(assessment, {types})}){
		    id, conversation_id, user_id, comment, assessed_status, date,
		    user{
          id, name,
          role{
            id, role
          }
        },
		    questions{
	        id, question_id, answer_id, answer_value, date, question{
	          id, question, order
	        }
	        answer{
	          id, answer
	        }
	      }
		  }
		`
		let result = await GraphQL.query({mutation});
		return result
	}

	static async setConversationToUser({conversationId, userId, force = true, skipEscalationVerificationByInactivity = false} = {}){
		let mutation = `#graphql
			mutation {
        setConversationToUser(conversationId:${conversationId}, userId:${userId}, force:${force}, skipEscalationVerificationByInactivity:${skipEscalationVerificationByInactivity}){
          id
        }
			}
		`
		let result = await GraphQL.query({mutation, addRootBrackets:false});
		return result;
	}

	static newConversationObject(){
		let conversation = {
			date:cuppa.dateToUTC(),
			status:Constants.conversationStatus.ACTIVE,
			last_assigned_user_id:UserCtrl.getUser().id,
		}
		return conversation;
	}

	static newAssessmentObject(){
		let assessment = {
			date:cuppa.dateToUTC(),
			assessed_status:1,
			comment:'',
		}
		return assessment;
	}

	static async sendMessage({message}){
		let types = {message:'multiline'}
		let mutation = `
			setConversationMessage(message:${cuppa.dataToGraphQLInput(message, {types, removeBrackets:false})}){
		    id, conversation_id, user_id, anonymous_user_id, message, date
		  }
		`
		return await GraphQL.query({mutation});
	}

	static async endConversation({conversation}){
		let mutation = `
			setConversation(conversation:{id:${conversation.id}, status:${conversation.status}}){
				id, organization_id, anonymous_user_id, status, date, last_assigned_user_id, description
			}
		`;
		let result = await GraphQL.query({mutation});
		if(!result?.data?.setConversation) return;
		//++ spread conversation to peer
			if(conversation?.anonymous_user_id){
				let target =  await UserCtrl.geyKey(conversation.anonymous_user_id, true);
				SocketManager.sendMessage({message:Constants.UPDATE_STORAGE, name:Constants.CONVERSATION_UPDATED, conversation:result.data.setConversation, target});
			}
		//--
	}

	static async deleteConversationMessageHistoryPrompt({conversationId}){
		let organization = OrganizationCtrl.getOrganization();
		if(!organization?.delete_message_history) return;
		await cuppa.wait(500)
		Globals.mainPortal.add("Alert",
			<Alert
				key={"Alert"}
				title={Language.value("Message")}
				message={Language.value('deleteMessageHistory')}
				cancel={Language.value("No")}
				accept={Language.value("Yes")}
				callback={async (res)=>{
					if(!res.value) return;
					ConversationsCtrl.deleteConversationMessageHistory({conversationId}).then();
				}}
			/>
		)
	}

	static async deleteConversationMessageHistory({conversationId}){
		let result = await GraphQL.query({mutation:`#graphql
			mutation {
				deleteConversationMessageHistory(conversationId: "${conversationId}"){
					id,
				}
			}
		`, addRootBrackets:false});
		if(result?.data?.deleteConversationMessageHistory?.id){
			NotificationCtrl.add({message:`Entire chat history for case # ${conversationId} has been deleted.`, openText:''})
		}
		return result;
	}

	static async getEarlierConversationDate(){
		const organization = await OrganizationCtrl.getOrganization();
		let result = await GraphQL.query({query:`#graphql
			{
				conversations(organizationId:"${organization.id}", quantity:1, orderBy:"date ASC"){
					data{
						date, status
					}
				}
			}
		`, addRootBrackets:false, printResult:false});
		let currentDate = new Date();
		let date = val(result, `data.conversations.data.0.date`, `${currentDate.getFullYear()-2}-01-01`);
		return date;
	}
}
